import { useMemo } from 'react';
import { CalendarRange, GitCompareArrows, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AvailablePeriods } from '@/lib/api';

interface PeriodOption {
  value: string;
  label: string;
  group: 'all' | 'quarter' | 'month';
}

const NONE_VALUE = '__none__';

function formatMonth(month: string): string {
  const [year, mo] = month.split('-');
  return `T${parseInt(mo!, 10)}/${year}`;
}

function formatQuarter(quarter: string): string {
  const [year, q] = quarter.split('-Q');
  return `${year} · Q${q}`;
}

export function buildPeriodOptions(available: AvailablePeriods | null): PeriodOption[] {
  const options: PeriodOption[] = [{ value: 'all', label: 'Tất cả các tháng', group: 'all' }];
  if (!available) return options;
  for (const q of available.quarters) {
    options.push({ value: q, label: formatQuarter(q), group: 'quarter' });
  }
  for (const m of available.months) {
    options.push({ value: m, label: formatMonth(m), group: 'month' });
  }
  return options;
}

export interface PeriodFilterProps {
  available: AvailablePeriods | null;
  period: string;
  compare: string;
  onPeriodChange: (value: string) => void;
  onCompareChange: (value: string) => void;
  className?: string;
}

export function PeriodFilter({
  available,
  period,
  compare,
  onPeriodChange,
  onCompareChange,
  className,
}: PeriodFilterProps) {
  const options = useMemo(() => buildPeriodOptions(available), [available]);
  const compareOptions = useMemo(
    () => options.filter((o) => o.value !== period),
    [options, period]
  );

  const compareActive = Boolean(compare);

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border bg-card p-3 shadow-sm sm:flex-row sm:items-center',
        className
      )}
    >
      <div className="flex flex-1 items-center gap-2">
        <CalendarRange className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Period:</span>
        <Select value={period} onValueChange={onPeriodChange}>
          <SelectTrigger className="h-8 w-[200px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <PeriodGroup options={options} group="all" />
            <PeriodGroup options={options} group="quarter" header="Theo Quý" />
            <PeriodGroup options={options} group="month" header="Theo Tháng" />
          </SelectContent>
        </Select>
      </div>

      <div className="hidden h-6 w-px bg-border sm:block" />

      <div className="flex flex-1 items-center gap-2">
        <GitCompareArrows
          className={cn('h-4 w-4 shrink-0', compareActive ? 'text-primary' : 'text-muted-foreground')}
        />
        <span className="text-xs font-medium text-muted-foreground">So sánh với:</span>
        <Select
          value={compare || NONE_VALUE}
          onValueChange={(v) => onCompareChange(v === NONE_VALUE ? '' : v)}
        >
          <SelectTrigger className="h-8 w-[200px] text-xs">
            <SelectValue placeholder="Không so sánh" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>
              <span className="text-muted-foreground">Không so sánh</span>
            </SelectItem>
            <PeriodGroup options={compareOptions} group="all" />
            <PeriodGroup options={compareOptions} group="quarter" header="Theo Quý" />
            <PeriodGroup options={compareOptions} group="month" header="Theo Tháng" />
          </SelectContent>
        </Select>
        {compareActive && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => onCompareChange('')}
            title="Xóa so sánh"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function PeriodGroup({
  options,
  group,
  header,
}: {
  options: PeriodOption[];
  group: PeriodOption['group'];
  header?: string;
}) {
  const filtered = options.filter((o) => o.group === group);
  if (filtered.length === 0) return null;
  return (
    <>
      {header && (
        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {header}
        </div>
      )}
      {filtered.map((o) => (
        <SelectItem key={o.value} value={o.value} className="text-xs">
          {o.label}
        </SelectItem>
      ))}
    </>
  );
}
